import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useGetPost } from '@onesource/services';
import { richHtmlSx } from '@onesource/components';
import { format } from 'date-fns';
import { ReactionBar } from './reaction-bar';
import { CommentThread } from './comment-thread';
import DOMPurify from 'dompurify';

/**
 * Post Detail Page
 * Shows a single post with full content, reactions, and comments
 */
export const PostDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams({ from: '/_protected/community-hub/posts/$id' });
  const { data: post, isLoading, error } = useGetPost(id);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load post: {error.message}</Alert>
      </Box>
    );
  }

  if (!post) {
    return (
      <Box p={3}>
        <Alert severity="error">Post not found</Alert>
      </Box>
    );
  }

  // Sanitize HTML content
  const sanitizedHtml = post.bodyHtml ? DOMPurify.sanitize(post.bodyHtml) : null;

  return (
    <Box p={3}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate({ to: '/community-hub' })}
          sx={{ cursor: 'pointer' }}
        >
          Community Hub
        </Link>
        <Typography variant="body2" color="text.primary">
          Post
        </Typography>
      </Breadcrumbs>

      {/* Post Card */}
      <Card>
        <CardContent>
          {/* Post Header */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Posted {format(new Date(post.createdAt), 'MMMM d, yyyy • h:mm a')}
              </Typography>
              {post.isPinned && <Chip label="Pinned" size="small" color="primary" sx={{ ml: 1 }} />}
            </Box>
            {post.postType && (
              <Chip label={post.postType} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
            )}
          </Box>

          {/* Post Title */}
          {post.title && (
            <Typography variant="h4" gutterBottom>
              {post.title}
            </Typography>
          )}

          {/* Post Body */}
          <Box mt={2} mb={3}>
            {sanitizedHtml ? (
              <Box sx={richHtmlSx} dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
            ) : (
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {post.body}
              </Typography>
            )}
          </Box>

          {/* Reaction Bar */}
          <Box display="flex" alignItems="center" gap={2}>
            <ReactionBar targetId={post.id} targetType="post" likeCount={post.likeCount} />
            <Typography variant="body2" color="text.secondary">
              • {post.commentCount} {post.commentCount === 1 ? 'comment' : 'comments'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Comments Section */}
      <Box mt={4}>
        <CommentThread postId={post.id} />
      </Box>
    </Box>
  );
};
